import * as React from "react";
import { Button } from "@jhool-io/fe-components";
import { Document, Page } from "react-pdf";
import ChevronLeftIcon from "../../icons/ChevronLeft";
import ChevronRightIcon from "../../icons/ChevronRight";
import DownloadIcon from "../Icons/Download";
import ListState from "../ListState/ListState";
import Loader from "../../shared-ui/Loader/Loader";
import { PDF_DOCUMENT_OPTIONS } from "../../lib/pdfWorker";
import { cn } from "../../utils/helpers";

const MAX_PAGE_WIDTH = 820;
const VIEWER_PADDING = 48;

export interface DocumentWorkspaceNavigation {
    current: number;
    total: number;
    onPrevious: () => void;
    onNext: () => void;
}

interface DocumentWorkspaceViewerProps {
    documentKey: string;
    data?: string;
    contentType?: string;
    isLoading?: boolean;
    isError?: boolean;
    title: string;
    subtitle?: React.ReactNode;
    badge?: React.ReactNode;
    navigation?: DocumentWorkspaceNavigation;
    onDownload?: () => void;
    renderSidePanel?: (
        pageNumber: number,
        pageCount: number
    ) => React.ReactNode;
}

export default function DocumentWorkspaceViewer({
    documentKey,
    data,
    contentType = "application/pdf",
    isLoading,
    isError,
    title,
    subtitle,
    badge,
    navigation,
    onDownload,
    renderSidePanel,
}: DocumentWorkspaceViewerProps) {
    const [numPages, setNumPages] = React.useState(0);
    const [pageNumber, setPageNumber] = React.useState(1);
    const [pageWidth, setPageWidth] = React.useState(MAX_PAGE_WIDTH);
    const [zoom, setZoom] = React.useState(1);
    const viewerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        setNumPages(0);
        setPageNumber(1);
        setZoom(1);
    }, [documentKey]);

    React.useEffect(() => {
        const viewer = viewerRef.current;
        if (!viewer || typeof ResizeObserver === "undefined") return undefined;

        const updatePageWidth = (width: number) => {
            const availableWidth = Math.floor(width - VIEWER_PADDING);
            if (availableWidth > 0) {
                setPageWidth(Math.min(availableWidth, MAX_PAGE_WIDTH));
            }
        };

        updatePageWidth(viewer.clientWidth);
        const resizeObserver = new ResizeObserver(([entry]) => {
            updatePageWidth(entry.contentRect.width);
        });
        resizeObserver.observe(viewer);

        return () => resizeObserver.disconnect();
    }, [isError, isLoading]);

    const goToPage = (page: number) => {
        if (!numPages) return;
        setPageNumber(Math.min(Math.max(page, 1), numPages));
    };

    const isImage = /^image/.test(contentType);

    return (
        <div className="flex h-[calc(100dvh-40px)] min-h-0 flex-col overflow-hidden bg-white">
            <header className="flex shrink-0 flex-col justify-between gap-2 border-b border-strokelight px-12 py-8 sm:flex-row sm:items-center">
                <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-2">
                        <h2
                            className="truncate text-base font-semibold text-text"
                            title={title}
                        >
                            {title}
                        </h2>
                        {badge}
                    </div>
                    {subtitle && (
                        <div className="mt-2 truncate text-xs text-textSec">
                            {subtitle}
                        </div>
                    )}
                </div>

                <div className="flex shrink-0 items-center justify-end gap-2">
                    {navigation && navigation.total > 1 && (
                        <div className="flex items-center gap-1 rounded-md border border-strokelight bg-white p-1">
                            <Button
                                variant="normal"
                                aria-label="Previous document"
                                className="flex size-6 items-center justify-center rounded border-0 bg-transparent p-0 text-textSec hover:bg-gray-100"
                                onClick={navigation.onPrevious}
                            >
                                <ChevronLeftIcon />
                            </Button>
                            <span className="min-w-16 text-center text-xs font-medium text-textSec">
                                {navigation.current} of {navigation.total}
                            </span>
                            <Button
                                variant="normal"
                                aria-label="Next document"
                                className="flex size-6 items-center justify-center rounded border-0 bg-transparent p-0 text-textSec hover:bg-gray-100"
                                onClick={navigation.onNext}
                            >
                                <ChevronRightIcon />
                            </Button>
                        </div>
                    )}
                    {onDownload && (
                        <Button
                            variant="normal"
                            onClick={onDownload}
                            disabled={!data}
                            className="flex h-8 items-center gap-1.5 rounded-md border-0 bg-[#EEF2F4] px-12 text-xs font-semibold text-text hover:bg-[#E2E8EC] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <DownloadIcon /> Download
                        </Button>
                    )}
                </div>
            </header>

            <div
                className={cn(
                    "grid min-h-0 flex-1 grid-cols-1 overflow-y-auto",
                    renderSidePanel &&
                        "lg:grid-cols-[minmax(0,1fr)_300px] lg:overflow-hidden"
                )}
            >
                <section className="flex min-h-[420px] min-w-0 flex-col bg-[#E9EDF2]">
                    {!isImage && !isLoading && !isError && (
                        <div className="sticky top-0 z-10 flex h-12 shrink-0 items-center justify-center gap-3 border-b border-[#D7DDE5] bg-white/95 px-12 shadow-sm backdrop-blur">
                            <Button
                                variant="normal"
                                aria-label="Previous page"
                                className="flex size-7 items-center justify-center rounded-md border border-stroke bg-white p-0 text-textSec hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                                disabled={pageNumber <= 1}
                                onClick={() => goToPage(pageNumber - 1)}
                            >
                                <ChevronLeftIcon className="size-4" />
                            </Button>
                            <span className="min-w-24 text-center text-sm font-semibold text-text">
                                Page {pageNumber} of{" "}
                                {numPages > 0 ? numPages : "—"}
                            </span>
                            <Button
                                variant="normal"
                                aria-label="Next page"
                                className="flex size-7 items-center justify-center rounded-md border border-stroke bg-white p-0 text-textSec hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                                disabled={!numPages || pageNumber >= numPages}
                                onClick={() => goToPage(pageNumber + 1)}
                            >
                                <ChevronRightIcon className="size-4" />
                            </Button>
                            <span className="h-5 w-px bg-strokelight" />
                            <Button
                                variant="normal"
                                aria-label="Zoom out"
                                className="flex size-7 items-center justify-center rounded-md border border-stroke bg-white p-0 text-base text-textSec hover:bg-gray-100 disabled:opacity-40"
                                disabled={zoom <= 0.75}
                                onClick={() =>
                                    setZoom((value) =>
                                        Math.max(0.75, value - 0.25)
                                    )
                                }
                            >
                                −
                            </Button>
                            <span className="min-w-10 text-center text-xs font-medium text-textSec">
                                {Math.round(zoom * 100)}%
                            </span>
                            <Button
                                variant="normal"
                                aria-label="Zoom in"
                                className="flex size-7 items-center justify-center rounded-md border border-stroke bg-white p-0 text-base text-textSec hover:bg-gray-100 disabled:opacity-40"
                                disabled={zoom >= 1.75}
                                onClick={() =>
                                    setZoom((value) =>
                                        Math.min(1.75, value + 0.25)
                                    )
                                }
                            >
                                +
                            </Button>
                        </div>
                    )}

                    <div
                        ref={viewerRef}
                        className="relative flex min-h-0 flex-1 items-start overflow-auto p-8 sm:p-16 lg:p-24"
                    >
                        {isLoading && <Loader />}
                        {isError && (
                            <ListState
                                isError
                                errorMsg="Could not load document at this time"
                                stateHelperText="Try reloading the page to fix this error"
                            />
                        )}
                        {data && isImage && (
                            <img
                                className="m-auto max-h-full max-w-full rounded-sm bg-white object-contain shadow-lg"
                                src={`data:${contentType};base64,${data}`}
                                alt={title}
                            />
                        )}
                        {data && !isImage && (
                            <div
                                className="mx-auto shrink-0 shadow-[0_12px_32px_rgba(15,23,42,0.18)]"
                                style={{ width: Math.round(pageWidth * zoom) }}
                            >
                                <Document
                                    file={`data:application/pdf;base64,${data}`}
                                    options={PDF_DOCUMENT_OPTIONS}
                                    loading={null}
                                    onLoadSuccess={({ numPages: count }) => {
                                        setNumPages(count);
                                        setPageNumber(1);
                                    }}
                                    error={
                                        <span className="flex h-32 items-center justify-center bg-white px-24 text-danger">
                                            Could not load this PDF
                                        </span>
                                    }
                                >
                                    <Page
                                        pageNumber={pageNumber}
                                        width={pageWidth}
                                        scale={zoom}
                                        renderAnnotationLayer={false}
                                        renderTextLayer={false}
                                    />
                                </Document>
                            </div>
                        )}
                    </div>
                </section>

                {renderSidePanel && (
                    <aside className="overflow-y-auto border-t border-strokelight bg-white p-12 lg:border-l lg:border-t-0">
                        {renderSidePanel(pageNumber, numPages)}
                    </aside>
                )}
            </div>
        </div>
    );
}
